import { Box, Checkbox, Tooltip } from "@mui/material";
import { useGridFilter } from "ag-grid-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ClearIcon from '@mui/icons-material/Clear';
import "./customFilter.css"

export const CustomDropdownFilter = (params) => {
    const { model, onModelChange, colDef, getValue } = params;
    const [selectAll, setSelectAll] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const refInput = useRef(null);
    const listData = colDef.listData;
    const [selectedValue, setSelectedValue] = useState(
        model?.filter === "" ? [...listData] : model?.filter ? model.filter : model ? model.split(",") : [...listData, "Blanks"]
    );
    const [filteredListData, setFilteredListData] = useState(listData);

    useEffect(() => {
        let tempListData = [...listData, "Blanks"]
        handleModelUpdate(tempListData.join(","))
        setFilteredListData(listData)
    }, [listData, params.colDef.listData])

    function evaluateCondition(values, item, condition) {
        const valLength = values.length
        if ((condition === 'blank') && Array.isArray(values) && valLength === 0) {
            return true;
        }
        else if ((condition === 'notBlank') && Array.isArray(values) && valLength !== 0) {
            return true;
        }
        return values.some(value => {
            const lowerValue = value.toLowerCase();
            const lowerItem = item.join(",");

            switch (condition) {
                case 'equals':
                    // Check if both strings (value and item) are exactly the same
                    return values.map(value => value.trim()).join(',').toLowerCase() === lowerItem.toLowerCase();

                case 'notEqual':
                    // Check if the concatenation of both strings (value and item) are not the same
                    return values.map(value => value.trim()).join(',').toLowerCase() !== lowerItem.toLowerCase();
                    return !(
                        Array.isArray(item) && Array.isArray(values) &&
                        values.length === item.length &&
                        values.every((v, idx) => v.toLowerCase() === item[idx].toLowerCase())
                    );

                case 'startsWith':
                    return lowerValue.startsWith(lowerItem);

                case 'contains':
                    return lowerValue.includes(lowerItem);

                case 'notContains':
                    return !lowerValue.includes(lowerItem);

                case 'blank':
                    return lowerValue === '' || lowerValue?.length === 0;

                case 'notBlank':
                    return lowerValue !== '' || lowerValue?.length !== 0;

                case 'endsWith':
                    return lowerValue.endsWith(lowerItem);

                default:
                    return false; // Returns false if the condition is not recognized
            }
        });

    }

    const doesFilterPass = useCallback(
        (params) => {
            const { node } = params;
            if (model === undefined) return;
            const filterText = model === null ? [] : model?.filter === "" ? (typeof listData === 'string' ? listData.split(",") : listData) : model?.filter ? model.filter : model.split(",").filter(item => item !== "");
            const condition = model?.type;
            let value = typeof getValue(node) !== "string" ? String(getValue(node)) : getValue(node)?.toLowerCase();
            value = value && value.includes('⊗⇔≈') ? value.split('⊗⇔≈') : value ? value.split(',') : [];
            const isBlanksOnly = filterText.length === 1 && filterText.includes("Blanks");

            if (isBlanksOnly) {
                // If "Blanks" is selected and no other filters are applied, show only empty rows
                return value === null || value === "" || value.length === 0;
            }
            if (Array.isArray(value) && value.length > 1) {
                value = value.filter(item => item !== "");
            }
            if (model === null) {
                return true;
            }
            else if (model === "") {
                return false;
            }
            else if (filterText.includes("Blanks")) {
                return true;
            }
            else if (value === null || value === "" || value === undefined || value[0] === "") {
                if (filterText.includes("Blanks")) {
                    return true;
                }
                else return false
            }
            else if (filterText.length == 1 && filterText[0] === 'Other') {
                if (value && value.some(val => !listData.includes(val))) {
                    return true
                }
                else return false
            }
            else {
                let filterResult = evaluateCondition(value, filterText, condition);
                return filterResult;
            }
        },
        [model]
    );

    const afterGuiAttached = useCallback(
        (params) => {
            if (!params || !params.suppressFocus) {
                refInput.current.focus();
            }
        },
        [refInput]
    );

    useGridFilter({
        doesFilterPass,
        afterGuiAttached,
    });

    const handleCheckboxChange = (value) => {
        setSelectedValue((prevSelectedValues) => {
            const newValue = prevSelectedValues.includes(value)
                ? prevSelectedValues.filter((val) => val !== value) // Fix: Use !== to remove the value
                : [...prevSelectedValues, value];

            handleModelUpdate(newValue.join(",")); // Update model with the new value list
            setSelectAll(false);
            return newValue; // Return the updated array
        });
    };

    const handleSelectAll = (value = "") => {
        if (value === "clear") {
            setSelectAll(true);
            setSelectedValue([...listData, "Other", "Blanks"])
            handleModelUpdate(null);
            return;
        }
        else {
            const newValue = !selectAll ? [...listData, "Other", "Blanks"] : [];
            setSelectAll((prevSelectAll) => !prevSelectAll);
            setSelectedValue(newValue);
            handleModelUpdate(newValue.join(","));
        }
    };


    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
        const temp = listData.filter((item) =>
            item.toLowerCase().includes(e.target.value.toLowerCase())
        );
        setFilteredListData(temp);
    };

    const handleModelUpdate = (value) => {
        if (value === null) {
            onModelChange(null);
        } else {
            onModelChange(value === "" ? "" : value);
        }
        // Force a refresh of the grid immediately after updating the model
        setTimeout(() => {
            if (params.api) {
                params.api.refreshCells({ force: true });
                params.api.redrawRows();
            }
        }, 0);
    };
    return (
        <Box className="filtercontainer">
            <Box className="inputContainer">
                <input
                    ref={refInput}
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Search..."
                    className="searchInput"
                />
                <Tooltip title="Clear Filter">
                    <ClearIcon className="clearIcon" onClick={() => handleSelectAll("clear")} />
                </Tooltip>
            </Box>
            <Box>
                {filteredListData?.length > 0 ? (
                    <Box className="selectAllContainer">
                        <Checkbox
                            size="small"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="checkbox"
                        />
                        <label className="selectAllLabel">Select All</label>
                    </Box>
                ) : (
                    <Box className="noMatchesContainer">
                        <label className="noMatchesLabel">No matches found</label>
                    </Box>
                )}
                <Box className="itemsContainer">
                    {filteredListData && filteredListData?.map((item, index) => (
                        <Box key={index} className="itemContainer">
                            <Checkbox
                                size="small"
                                checked={selectedValue.includes(item)}
                                onChange={() => handleCheckboxChange(item)}
                                className="checkbox"
                            />
                            <label className="itemLabel">{item}</label>
                        </Box>
                    ))}
                </Box>
                {filteredListData.length > 0 && (
                    <Box className="selectAllContainer">
                        <Checkbox
                            size="small"
                            checked={selectedValue.includes("Blanks")}
                            onChange={() => handleCheckboxChange("Blanks")}
                            className="checkbox"
                        />
                        <label className="blanksLabel">Blanks</label>
                    </Box>
                )}
            </Box>
        </Box>
    );

};
